// src/pages/labita/master-budget-tanam/MasterBudgetTanam.jsx
import React, { useState, useEffect, useRef } from "react";
import {
    Typography,
    Box,
    Breadcrumbs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Link as MuiLink,
    TablePagination,
    Chip,
    IconButton,
    MenuItem,
    Stack,
    Snackbar,
    FormControl,
    InputLabel,
    Select,
    Button,
    TextField
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import SearchIcon from '@mui/icons-material/Search';
import * as XLSX from "xlsx";

import { useTheme } from "@mui/material/styles";
import useAuthCheck from "../../../hooks/useAuthCheck";
import { useOutletContext, useNavigate } from "react-router-dom";
import { database } from "../../../firebase";
import { ref, onValue, push, update } from "firebase/database";

// Helper: ubah onValue menjadi Promise
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

// Helper function to convert Excel date number to YYYY-MM-DD string
const excelDateToJSDate = (excelDate) => {
    if (typeof excelDate === 'number' && excelDate > 0) {
        const date = new Date(Math.round((excelDate - 25569) * 86400 * 1000));
        return date.toISOString().split('T')[0];
    }
    return excelDate;
};

// Styles for TableHead Cells
const headerCellStyle = {
    border: '1px solid rgba(224, 224, 224, 1)',
    position: 'sticky',
    backgroundColor: 'white',
    zIndex: 2
};

const subHeaderCellStyle = {
    border: '1px solid rgba(224, 224, 224, 1)',
    position: 'sticky',
    backgroundColor: 'white',
    zIndex: 1,
    top: 56
};

function MasterBudgetTanam() {
    const { user, loading: authLoading } = useAuthCheck();
    const theme = useTheme();
    const navigate = useNavigate();
    const { breadcrumbs } = useOutletContext();

    const [dataList, setDataList] = useState([]);
    const [originalDataList, setOriginalDataList] = useState([]);
    const [loadingData, setLoadingData] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const fileInputRef = useRef(null);
    const [importLoading, setImportLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });
    const [showFilterPanel, setShowFilterPanel] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // State untuk data konfigurasi dropdown
    const [kodeWilayahList, setKodeWilayahList] = useState([]);
    const [statusLokasiList, setStatusLokasiList] = useState([]);
    const [helperData, setHelperData] = useState({});

    // Filter states
    const [filterStatusLokasi, setFilterStatusLokasi] = useState('');
    const [filterTahun, setFilterTahun] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            setLoadingData(true);
            try {
                // Fetch main data from db_labita/db_master_budget_tanam
                const budgetTanamRef = ref(database, "db_labita/db_master_budget_tanam");
                const budgetTanamSnapshot = await onValuePromise(budgetTanamRef);
                const budgetTanamData = budgetTanamSnapshot.val();
                const loadedBudgetTanam = [];
                if (budgetTanamData) {
                    Object.keys(budgetTanamData).forEach(key => {
                        loadedBudgetTanam.push({ 
                            firebaseKey: key, 
                            ...budgetTanamData[key]
                        });
                    });
                }
                setOriginalDataList(loadedBudgetTanam);
                setDataList(loadedBudgetTanam);

                // Fetch lookup data for Kode Wilayah
                const kodeWilayahConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_kode_wilayah");
                const kodeWilayahConfigSnapshot = await onValuePromise(kodeWilayahConfigRef);
                const kodeWilayahConfigData = kodeWilayahConfigSnapshot.val();
                if (kodeWilayahConfigData) {
                    const list = Object.keys(kodeWilayahConfigData).map(key => ({
                        id: key,
                        nama_kode_wilayah: kodeWilayahConfigData[key].nama_kode_wilayah,
                    }));
                    setKodeWilayahList(list);
                }

                // Fetch lookup data for Status Lokasi
                const statusLokasiConfigRef = ref(database, "db_config/db_fresh_pineapple/tb_status_lokasi");
                const statusLokasiConfigSnapshot = await onValuePromise(statusLokasiConfigRef);
                const statusLokasiConfigData = statusLokasiConfigSnapshot.val();
                if (statusLokasiConfigData) {
                    const list = Object.keys(statusLokasiConfigData).map(key => ({
                        id: key,
                        nama_status_lokasi: statusLokasiConfigData[key].nama_status_lokasi,
                        indicator: statusLokasiConfigData[key].indicator || '#FFFFFF'
                    }));
                    setStatusLokasiList(list);
                }

                // Fetch data from db_config/db_helper
                const helperRef = ref(database, "db_config/db_helper/tb_wilayah_plant_care");
                const helperSnapshot = await onValuePromise(helperRef);
                const helperRawData = helperSnapshot.val();
                if (helperRawData) {
                    setHelperData(helperRawData);
                } else {
                    setHelperData({});
                }

            } catch (error) {
                console.error("Error fetching data:", error);
                setSnackbar({
                    open: true,
                    message: "Gagal memuat data.",
                    severity: "error",
                });
            } finally {
                setLoadingData(false);
            }
        };

        if (user) {
            fetchData();
        }
    }, [user]);

    // Effect untuk melakukan pencarian saat searchQuery berubah
    useEffect(() => {
        if (searchQuery) {
            const filtered = originalDataList.filter(item => 
                item.lokasi && item.lokasi.toLowerCase().includes(searchQuery.toLowerCase())
            );
            setDataList(filtered);
        } else {
            setDataList(originalDataList);
        }
        setPage(0);
    }, [searchQuery, originalDataList]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleMenuClick = (event) => {
        setMenuAnchor(event.currentTarget);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

const handleImportExcel = async (event) => {
    const file = event.target.files[0];
    if (!file) {
        setSnackbar({
            open: true,
            message: "Tidak ada file yang dipilih.",
            severity: "warning",
        });
        return;
    }

    setImportLoading(true);
    setSnackbar({
        open: true,
        message: "Memulai proses import...",
        severity: "info",
    });

    const reader = new FileReader();
    reader.onload = async (e) => {
        try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: "array" });

            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            
            const range = XLSX.utils.decode_range(worksheet['!ref']);
            const startRow = 6; // Baris 7 (B7)
            
            const budgetTanamRef = ref(database, "db_labita/db_master_budget_tanam");
            const existingBudgetTanamSnapshot = await onValuePromise(budgetTanamRef);
            const existingBudgetTanamData = existingBudgetTanamSnapshot.val() || {};

            // Create a map for quick lookup: id_budget_tanam -> array of existing records
            // Untuk mengecek apakah kombinasi (lokasi + awal_perawatan + tanggal_rencana_tanam) sudah ada
            const existingRecordsMap = new Map();
            
            Object.keys(existingBudgetTanamData).forEach(fbKey => {
                const record = existingBudgetTanamData[fbKey];
                if (record.id_budget_tanam) {
                    const keyForComparison = `${record.id_budget_tanam}_${record.tanggal_rencana_tanam || ''}`;
                    if (!existingRecordsMap.has(keyForComparison)) {
                        existingRecordsMap.set(keyForComparison, fbKey);
                    }
                }
            });

            const importTimestamp = new Date().toISOString().split('T')[0];

            let importedCount = 0;
            let updatedCount = 0;
            let ignoredCount = 0;

            // Iterasi dari baris 7 hingga akhir data
            for (let row = startRow; row <= range.e.r; row++) {
                const getCellValue = (col, rowNum) => {
                    const cellAddress = XLSX.utils.encode_cell({ c: col, r: rowNum });
                    const cell = worksheet[cellAddress];
                    return cell ? cell.v : '';
                };

                const lokasi = getCellValue(1, row);
                
                if (!lokasi || lokasi.toString().trim() === '') {
                    ignoredCount++;
                    continue;
                }

                // Helper function untuk format angka dengan default '-'
                const formatNumberWithDash = (value) => {
                    if (value === undefined || value === null || value === '' || value === '-') {
                        return '-';
                    }
                    
                    if (typeof value === 'string') {
                        let cleanedValue = value.toString().trim().replace(',', '.');
                        if (cleanedValue === '' || cleanedValue === '-') {
                            return '-';
                        }
                        const num = parseFloat(cleanedValue);
                        return !isNaN(num) ? num.toString() : '-';
                    }
                    
                    if (typeof value === 'number') {
                        return value.toString();
                    }
                    
                    return '-';
                };

                // Helper function untuk format tanggal ke DD-MM-YYYY dengan default '-'
                const formatDateToDDMMYYYY = (value) => {
                    if (!value || value === '' || value === '-') {
                        return '-';
                    }
                    
                    try {
                        let date;
                        
                        if (typeof value === 'number') {
                            date = new Date(Math.round((value - 25569) * 86400 * 1000));
                        } else if (typeof value === 'string') {
                            const parts = value.split('/');
                            if (parts.length === 3) {
                                const day = parts[0].padStart(2, '0');
                                const month = parts[1].padStart(2, '0');
                                const year = parts[2];
                                return `${day}-${month}-${year}`;
                            }
                            
                            const parts2 = value.split('-');
                            if (parts2.length === 3) {
                                const day = parts2[2].padStart(2, '0');
                                const month = parts2[1].padStart(2, '0');
                                const year = parts2[0];
                                return `${day}-${month}-${year}`;
                            }
                            
                            if (value.trim() === '-') {
                                return '-';
                            }
                            
                            date = new Date(value);
                        } else {
                            return '-';
                        }
                        
                        if (isNaN(date.getTime())) {
                            return '-';
                        }
                        
                        const day = String(date.getDate()).padStart(2, '0');
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const year = date.getFullYear();
                        
                        return `${day}-${month}-${year}`;
                    } catch (error) {
                        console.error("Error formatting date:", error);
                        return '-';
                    }
                };

                // Helper function untuk format tanggal ke ddmmyyyy (untuk ID)
                const formatToDDMMYYYYForID = (dateStr) => {
                    if (dateStr === '-') return '-';
                    return dateStr.replace(/-/g, '');
                };

                // Helper function untuk format string dengan default '-'
                const formatStringWithDash = (value) => {
                    if (!value || value === '' || value === '-') {
                        return '-';
                    }
                    return value.toString().trim();
                };

                // Ambil awal_perawatan untuk pembuatan ID
                const awalPerawatanRaw = getCellValue(12, row);
                const awalPerawatanFormatted = formatDateToDDMMYYYY(awalPerawatanRaw);
                const tanggalDDMMYYYY = formatToDDMMYYYYForID(awalPerawatanFormatted);
                
                // Ambil tanggal_rencana_tanam
                const tanggalRencanaTanamRaw = getCellValue(24, row);
                const tanggalRencanaTanamFormatted = formatDateToDDMMYYYY(tanggalRencanaTanamRaw);
                
                // Buat id_budget_tanam dari gabungan lokasi dan tanggal ddmmyyyy (awal_perawatan)
                const idBudgetTanam = tanggalDDMMYYYY !== '-' 
                    ? `${lokasi.toString().trim()}_${tanggalDDMMYYYY}` 
                    : lokasi.toString().trim();

                // Buat key untuk pengecekan duplikat: kombinasi id_budget_tanam + tanggal_rencana_tanam
                const duplicateCheckKey = `${idBudgetTanam}_${tanggalRencanaTanamFormatted || ''}`;
                
                // Cek jika data sudah ada dengan kombinasi (lokasi + awal_perawatan + tanggal_rencana_tanam) yang sama
                if (existingRecordsMap.has(duplicateCheckKey)) {
                    // Skip jika data sudah ada dengan kombinasi yang sama
                    ignoredCount++;
                    console.log(`Data dengan id ${idBudgetTanam} dan tanggal rencana tanam ${tanggalRencanaTanamFormatted} sudah ada, diabaikan`);
                    continue;
                }

                // Siapkan data dengan struktur yang sesuai
                const dataToSave = {
                    id_budget_tanam: idBudgetTanam, // Tetap simpan id_budget_tanam di dalam data
                    lokasi: formatStringWithDash(lokasi),
                    struktur_plant: formatStringWithDash(getCellValue(4, row)),
                    bruto: formatNumberWithDash(getCellValue(5, row)),
                    netto: formatNumberWithDash(getCellValue(6, row)),
                    aktif: formatNumberWithDash(getCellValue(7, row)),
                    varietas: formatStringWithDash(getCellValue(8, row)),
                    kelas: formatStringWithDash(getCellValue(9, row)),
                    jenis: formatStringWithDash(getCellValue(10, row)),
                    status: formatStringWithDash(getCellValue(11, row)),
                    awal_perawatan: awalPerawatanFormatted,
                    perubahan_status: formatDateToDDMMYYYY(getCellValue(13, row)),
                    awal_status: formatDateToDDMMYYYY(getCellValue(14, row)),
                    panen_base_umur_standar: formatDateToDDMMYYYY(getCellValue(15, row)),
                    panen_forecast: formatDateToDDMMYYYY(getCellValue(17, row)),
                    panen_akhir: formatDateToDDMMYYYY(getCellValue(18, row)),
                    awal_bk: formatDateToDDMMYYYY(getCellValue(20, row)),
                    sts: formatStringWithDash(getCellValue(21, row)),
                    kode_bibit: formatStringWithDash(getCellValue(22, row)),
                    luas_tanaman: formatNumberWithDash(getCellValue(23, row)),
                    tanggal_rencana_tanam: tanggalRencanaTanamFormatted,
                    last_imported_at: importTimestamp
                };

                // Simpan data dengan auto-generated key dari Firebase (push())
                const newRecordRef = push(ref(database, "db_labita/db_master_budget_tanam"));
                await update(newRecordRef, dataToSave);
                
                // Update map untuk pengecekan duplikat selanjutnya dalam batch ini
                existingRecordsMap.set(duplicateCheckKey, newRecordRef.key);
                
                importedCount++;
            }

            setSnackbar({
                open: true,
                message: `Impor selesai! Berhasil menambahkan ${importedCount} data baru. ${ignoredCount} data diabaikan (data sudah ada atau tidak lengkap).`,
                severity: "success",
            });
            
            // Re-fetch data to update the table display
            const updatedBudgetTanamSnapshot = await onValuePromise(budgetTanamRef);
            const updatedBudgetTanamData = updatedBudgetTanamSnapshot.val() || {};
            const updatedLoadedBudgetTanam = [];
            Object.keys(updatedBudgetTanamData).forEach(key => {
                updatedLoadedBudgetTanam.push({ 
                    firebaseKey: key, // Sekarang key adalah auto-generated dari Firebase
                    ...updatedBudgetTanamData[key] 
                });
            });
            setDataList(updatedLoadedBudgetTanam);
            setOriginalDataList(updatedLoadedBudgetTanam);

        } catch (error) {
            console.error("Gagal impor Excel:", error);
            setSnackbar({
                open: true,
                message: "Terjadi kesalahan saat mengimpor data dari Excel.",
                severity: "error",
            });
        } finally {
            event.target.value = null;
            handleMenuClose();
            setImportLoading(false);
        }
    };

    reader.readAsArrayBuffer(file);
};

    const handleExport = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Export akan dikembangkan.",
            severity: "info",
        });
        handleMenuClose();
    };

    const handlePrint = () => {
        setSnackbar({
            open: true,
            message: "Fungsi Print akan dikembangkan.",
            severity: "info",
        });
        handleMenuClose();
    };

    const handleToggleFilterPanel = () => {
        setShowFilterPanel(prev => !prev);
    };

    const handleFilterData = () => {
        let filtered = [...originalDataList];

        if (filterStatusLokasi) {
            filtered = filtered.filter(item => item.status === filterStatusLokasi);
        }

        setDataList(filtered);
        setPage(0);
    };

    const handleResetFilter = () => {
        setFilterStatusLokasi('');
        setFilterTahun('');
        setDataList(originalDataList);
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
    };

    // Helper function untuk format angka dengan pemisah ribuan
const formatNumberWithSeparator = (value) => {
    if (value === undefined || value === null || value === '' || value === '-') {
        return '-';
    }
    
    // Jika sudah string angka, coba parse
    if (typeof value === 'string') {
        // Jika sudah "-", kembalikan "-"
        if (value === '-') return '-';
        
        const num = parseFloat(value.replace(/,/g, '.'));
        if (!isNaN(num)) {
            return num.toLocaleString('id-ID', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            });
        }
    }
    
    // Jika number
    if (typeof value === 'number') {
        return value.toLocaleString('id-ID', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    }
    
    return value;
};

    

    const displayedDataList = dataList;

    return (
        <Box sx={{ pb: 3 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                {breadcrumbs && breadcrumbs.map((crumb, index) => (
                    crumb.path ? (
                        <MuiLink
                            key={index}
                            underline="hover"
                            color="inherit"
                            onClick={() => navigate(crumb.path)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {crumb.label}
                        </MuiLink>
                    ) : (
                        <Typography key={index} color="text.primary">
                            {crumb.label}
                        </Typography>
                    )
                ))}
            </Breadcrumbs>

            {showFilterPanel && (
                <Paper sx={{ width: '100%', overflow: 'hidden', p: 2, mb: 2 }}>
                    <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
                        Filter
                    </Typography>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status Lokasi</InputLabel>
                            <Select
                                value={filterStatusLokasi}
                                label="Status Lokasi"
                                onChange={(e) => setFilterStatusLokasi(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>None</em>
                                </MenuItem>
                                {statusLokasiList.map((status) => (
                                    <MenuItem key={status.id} value={status.nama_status_lokasi}>
                                        {status.nama_status_lokasi}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button variant="outlined" onClick={handleResetFilter}>
                            Reset Filter
                        </Button>
                        <Button variant="contained" onClick={handleFilterData}>
                            Filter Data
                        </Button>
                    </Stack>
                </Paper>
            )}

            <Paper sx={{ width: '100%', overflow: 'hidden', p: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                    <Typography variant="h6" component="h2">
                        Master Budget Tanam
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                            size="small"
                            placeholder="Cari berdasarkan lokasi..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                            }}
                            sx={{ width: 250 }}
                        />
                        <IconButton color="primary" size="small" onClick={handleToggleFilterPanel}>
                            {showFilterPanel ? <FilterAltOutlinedIcon /> : <FilterAltOffOutlinedIcon />}
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handleImportButtonClick} disabled={importLoading}>
                            {importLoading ? <CircularProgress size={20} /> : <FileUploadOutlinedIcon />}
                        </IconButton>
                        <input
                            type="file"
                            accept=".xlsx, .xls"
                            ref={fileInputRef}
                            style={{ display: "none" }}
                            onChange={handleImportExcel}
                        />
                        <IconButton color="primary" size="small" onClick={handleExport}>
                            <FileDownloadOutlinedIcon />
                        </IconButton>
                        <IconButton color="primary" size="small" onClick={handlePrint}>
                            <PrintOutlinedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                <TableContainer sx={{ maxHeight: 600, overflow: 'auto' }}>
                    <Table stickyHeader aria-label="master budget tanam table">
                        <TableHead>
                            {/* Baris Pertama - Header Utama */}
                            <TableRow>
                                <TableCell rowSpan={2} align="center" sx={headerCellStyle}>Lokasi</TableCell>
                                <TableCell colSpan={1} align="center" sx={headerCellStyle}>Info Area</TableCell>
                                <TableCell colSpan={3} align="center" sx={headerCellStyle}>Luas</TableCell>
                                <TableCell colSpan={3} align="center" sx={headerCellStyle}>Bibit</TableCell>
                                <TableCell colSpan={4} align="center" sx={headerCellStyle}>Status</TableCell>
                                <TableCell colSpan={3} align="center" sx={headerCellStyle}>Perhitungan Akhir Status</TableCell>
                                <TableCell colSpan={1} align="center" sx={headerCellStyle}>Potensi</TableCell>
                                <TableCell colSpan={4} align="center" sx={headerCellStyle}>Rencana Tanam 1</TableCell>
                            </TableRow>
                            
                            {/* Baris Kedua - Sub Header */}
                            <TableRow>
                                {/* Info Area */}
                                <TableCell align="center" sx={subHeaderCellStyle}>Struktur Plant</TableCell>
                                
                                {/* Luas */}
                                <TableCell align="center" sx={subHeaderCellStyle}>Bruto</TableCell>
                                <TableCell align="center" sx={subHeaderCellStyle}>Netto</TableCell>
                                <TableCell align="center" sx={subHeaderCellStyle}>Aktif</TableCell>
                                
                                {/* Bibit */}
                                <TableCell align="center" sx={subHeaderCellStyle}>Varietas</TableCell>
                                <TableCell align="center" sx={subHeaderCellStyle}>Kelas</TableCell>
                                <TableCell align="center" sx={subHeaderCellStyle}>Jenis</TableCell>
                                
                                {/* Status */}
                                <TableCell align="center" sx={subHeaderCellStyle}>Status</TableCell>
                                <TableCell align="center" sx={subHeaderCellStyle}>Awal Perawatan</TableCell>
                                <TableCell align="center" sx={subHeaderCellStyle}>Perubahan Status</TableCell>
                                <TableCell align="center" sx={subHeaderCellStyle}>Awal Status</TableCell>
                                
                                {/* Perhitungan Akhir Status */}
                                <TableCell align="center" sx={subHeaderCellStyle}>Panen Base Umur Standar</TableCell>
                                <TableCell align="center" sx={subHeaderCellStyle}>Panen Forecast</TableCell>
                                <TableCell align="center" sx={subHeaderCellStyle}>Panen Akhir</TableCell>
                                
                                {/* Potensi */}
                                <TableCell align="center" sx={subHeaderCellStyle}>Awal BK</TableCell>
                                
                                {/* Rencana Tanam 1 */}
                                <TableCell align="center" sx={subHeaderCellStyle}>Sts</TableCell>
                                <TableCell align="center" sx={subHeaderCellStyle}>Kode Bibit</TableCell>
                                <TableCell align="center" sx={subHeaderCellStyle}>Luas Tanaman</TableCell>
                                <TableCell align="center" sx={subHeaderCellStyle}>Tanggal Rencana Tanam</TableCell>
                            </TableRow>
                        </TableHead>

<TableBody>
    {displayedDataList.length > 0 ? (
        displayedDataList
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((row) => {
                // Lookup nama_status_lokasi dan indicator
                const statusLokasiItem = statusLokasiList.find(
                    (item) => item.nama_status_lokasi === row.status
                );
                const displayStatusLokasi = statusLokasiItem?.nama_status_lokasi || row.status || '-';
                const statusIndicatorColor = statusLokasiItem?.indicator || '#FFFFFF';

                return (
                    <TableRow key={row.firebaseKey}>
                        {/* Lokasi */}
                        <TableCell>{row.lokasi || '-'}</TableCell>
                        
                        {/* Info Area - Struktur Plant */}
                        <TableCell>{row.struktur_plant || '-'}</TableCell>
                        
                        {/* Luas */}
                        <TableCell>{row.bruto !== undefined ? formatNumberWithSeparator(row.bruto) : '-'}</TableCell>
                        <TableCell>{row.netto !== undefined ? formatNumberWithSeparator(row.netto) : '-'}</TableCell>
                        <TableCell>{row.aktif !== undefined ? formatNumberWithSeparator(row.aktif) : '-'}</TableCell>
                        
                        {/* Bibit */}
                        <TableCell>{row.varietas || '-'}</TableCell>
                        <TableCell>{row.kelas || '-'}</TableCell>
                        <TableCell>{row.jenis || '-'}</TableCell>
                        
                        {/* Status */}
                        <TableCell>
                            {displayStatusLokasi && displayStatusLokasi !== '-' ? (
                                <Chip
                                    label={displayStatusLokasi}
                                    style={{ backgroundColor: statusIndicatorColor, color: theme.palette.getContrastText(statusIndicatorColor) }}
                                    size="small"
                                />
                            ) : '-'}
                        </TableCell>
                        <TableCell>{row.awal_perawatan || '-'}</TableCell>
                        <TableCell>{row.perubahan_status || '-'}</TableCell>
                        <TableCell>{row.awal_status || '-'}</TableCell>
                        
                        {/* Perhitungan Akhir Status */}
                        <TableCell>{row.panen_base_umur_standar || '-'}</TableCell>
                        <TableCell>{row.panen_forecast || '-'}</TableCell>
                        <TableCell>{row.panen_akhir || '-'}</TableCell>
                        
                        {/* Potensi - adalah tanggal */}
                        <TableCell>{row.awal_bk || '-'}</TableCell>
                        
                        {/* Rencana Tanam 1 */}
                        <TableCell>{row.sts || '-'}</TableCell>
                        <TableCell>{row.kode_bibit || '-'}</TableCell>
                        <TableCell>{row.luas_tanaman !== undefined ? formatNumberWithSeparator(row.luas_tanaman) : '-'}</TableCell>
                        <TableCell>{row.tanggal_rencana_tanam || '-'}</TableCell>
                    </TableRow>
                );
            })
    ) : (
        <TableRow>
            <TableCell colSpan={22} align="center">
                {searchQuery ? 'Tidak ada data yang sesuai dengan pencarian' : 'Tidak ada data master budget tanam.'}
            </TableCell>
        </TableRow>
    )}
</TableBody>

                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={displayedDataList.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default MasterBudgetTanam;